import React from 'react';
import styled from 'styled-components';
import { GrowthNav } from './GrowthNav';
import { useGrowthTasks, useGrowthTaskUpdate } from '../../hooks/useGrowthTasks';
import type { GrowthTask } from '../../hooks/useGrowthTasks';

const Page = styled.div`
  background: var(--background);
  min-height: 100%;
`;

const Card = styled.div`
  padding: 1.25rem;
  border-radius: 14px;
  background: var(--surface);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--outline);
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
  th,
  td {
    padding: 0.5rem 0.75rem;
    text-align: left;
    border-bottom: 1px solid var(--outline);
  }
  th {
    font-weight: 600;
    color: var(--onSurfaceVariant);
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.6rem;
      font-size: 0.85rem;
    }
  }
`;

const Select = styled.select`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  border: 1px solid var(--outline);
  font-size: 0.85rem;
`;

const ImpactBadge = styled.span<{ $impact: string }>`
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  background: ${({ $impact }) =>
    $impact === 'high' ? 'var(--errorContainer)' : $impact === 'med' ? 'var(--warningContainer)' : 'var(--successContainer)'};
  color: ${({ $impact }) =>
    $impact === 'high' ? 'var(--error)' : $impact === 'med' ? 'var(--warning)' : 'var(--success)'};
`;

export default function GrowthTasksPage() {
  const { data: tasks = [], isLoading } = useGrowthTasks();
  const updateTask = useGrowthTaskUpdate();

  const handleStatusChange = (id: string, status: GrowthTask['status']) => {
    updateTask.mutate({ id, updates: { status } });
  };

  if (isLoading) return <Page><GrowthNav /><p>Loading tasks…</p></Page>;

  return (
    <Page>
      <GrowthNav />
      <SectionTitle style={{ marginTop: 0 }}>Tasks</SectionTitle>
      <Card>
        <TableWrap>
          <Table>
            <thead>
            <tr>
              <th>Title</th>
              <th>Impact</th>
              <th>Status</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.id}>
                <td>
                  <strong>{t.title}</strong>
                  <div style={{ fontSize: '0.8rem', color: 'var(--onSurfaceVariant)' }}>{t.description}</div>
                </td>
                <td><ImpactBadge $impact={t.impact}>{t.impact}</ImpactBadge></td>
                <td>
                  <Select
                    value={t.status}
                    onChange={(e) => handleStatusChange(t.id, e.target.value as GrowthTask['status'])}
                    disabled={updateTask.isPending}
                  >
                    <option value="todo">Todo</option>
                    <option value="doing">Doing</option>
                    <option value="done">Done</option>
                    <option value="snoozed">Snoozed</option>
                  </Select>
                </td>
                <td>{t.source}</td>
              </tr>
            ))}
          </tbody>
          </Table>
        </TableWrap>
      </Card>
    </Page>
  );
}
